import { Check, GitMerge, X } from "lucide-react";

import { useEvent } from "../../changelog-item-context";

import ApprovalButtons from "./ApprovalButtons";
import { useCanApprove } from "./hooks/useCanApprove";

import { Button } from "@/components/ui/button";
import UserAttribution from "@/components/UserAttribution";
import { useApproveChangelog, useDeployChangelog } from "@/queries/folder-entities";

interface ApprovalContentProps {
  isApproved?: boolean;
  onApprove(result: boolean): void;
}

const Separator = () => <span className="text-gray-300">·</span>;

const StatusBadge = ({
  icon: Icon,
  text,
  colorClass,
}: {
  icon: React.ElementType;
  text: string;
  colorClass: string;
}) => (
  <span className={`inline-flex items-center gap-1 ${colorClass}`}>
    <Icon className="h-3 w-3" />
    {text}
  </span>
);

const BaseLayout = ({ children, userEmail }: { children: React.ReactNode; userEmail?: string }) => (
  <div className="mt-2 flex items-center gap-2 text-[13px] text-gray-500">
    <UserAttribution email={userEmail} />
    <Separator />
    {children}
  </div>
);

const ApprovalContent = ({ isApproved, onApprove: _onApprove }: ApprovalContentProps) => {
  const event = useEvent();
  const { approved_by_user, user, merged, approved_by } = event;
  const { canApprove } = useCanApprove();
  const { mutate: approveChangelog, isLoading: isApproving } = useApproveChangelog({
    changelogId: event.id,
  });
  const { mutate: deployChangelog, isLoading: isDeploying } = useDeployChangelog({
    changelogId: event.id,
  });

  const handleApprove = () => {
    approveChangelog({ approve: true });
  };

  const handleReject = () => {
    approveChangelog({ approve: false });
  };

  const handleDeploy = () => {
    deployChangelog({});
  };

  const isDenied = !isApproved && approved_by;

  if (isDenied) {
    return (
      <BaseLayout userEmail={user?.email}>
        <StatusBadge icon={X} text="Denied by" colorClass="text-red-500" />
        <UserAttribution email={approved_by_user?.email} />
      </BaseLayout>
    );
  }

  if (isApproved && merged) {
    return (
      <BaseLayout userEmail={user?.email}>
        <StatusBadge icon={Check} text="Approved by" colorClass="text-green-600" />
        <UserAttribution email={approved_by_user?.email} />
        <Separator />
        <StatusBadge icon={GitMerge} text="Deployed" colorClass="text-violet-600" />
      </BaseLayout>
    );
  }

  if (isApproved) {
    return (
      <BaseLayout userEmail={user?.email}>
        <StatusBadge icon={Check} text="Approved by" colorClass="text-green-600" />
        <UserAttribution email={approved_by_user?.email} />
        <Button
          onClick={handleDeploy}
          variant="outline"
          size="sm"
          className="ml-1 h-6 gap-1 border-violet-200 bg-violet-50 px-2 text-xs text-violet-700 hover:bg-violet-100"
          disabled={isDeploying}
        >
          <GitMerge className="h-3 w-3" />
          Deploy
        </Button>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout userEmail={user?.email}>
      <span className="text-amber-600">Pending approval</span>
      {canApprove && (
        <ApprovalButtons onApprove={handleApprove} onReject={handleReject} disabled={isApproving} />
      )}
    </BaseLayout>
  );
};

export default ApprovalContent;
